/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import React, { useContext, useEffect } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import {
  getMissionLocationNameTuplesFor,
  LocationPropertyKeys,
  mapStringToMissionLocationKey,
  MissionLocationKeys,
  OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
  OperationalDataLocationRecord,
} from '../../../../models/genericElements/operationalData/missionLocation';
import RadioList from '../../../customs/radioList/RadioList';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { useFreshCallback } from '../../../../utils/hooks';
import { NodeType } from '../../../../backendModels/report.model';
import { Draft } from 'immer';
import { InputState } from '../../../../backendModels/general.model';
import { isGenericRecordDeletable } from '../../../../models/generic';
import { OperationalDataTransportLocationRecord } from '../../../../models/genericElements/operationalData/transportLocation';
import {
  changePropertyAndMarkTouched,
  getTouchedClassName,
  handleUntouchedSingleSelectFieldUpdates,
  handleUntouchedTextFieldUpdates,
} from '../../../../utils/recordWithTouched';
import { NUMBER_LANGUAGE } from '../../../../utils/util';
import _ from 'lodash';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { ApplicationConfig } from '../../../../config/application.config';

const locationItems = getMissionLocationNameTuplesFor([
  MissionLocationKeys.wohnung,
  MissionLocationKeys.altenheim,
  MissionLocationKeys.arbeitsplatz,
  MissionLocationKeys.sportstaette,
  MissionLocationKeys.arztpraxis,
  MissionLocationKeys.krankenhaus,
  MissionLocationKeys.geburtshaus,
  MissionLocationKeys.oeffentlicherraum,
  MissionLocationKeys.strasse,
  MissionLocationKeys.schule,
  MissionLocationKeys.bildungseinrichtung,
  MissionLocationKeys.massenveranstaltung,
  MissionLocationKeys.sonstige,
]);

interface MissionLocationProps {
  readonly nodeType: NodeType;
}

export default function MissionLocation({ nodeType }: MissionLocationProps) {
  const { findRecordOrDefault, adaptRecord, missionData, missionDataUpdate, findNodes } = useContext(ReportsAPIContext);

  const record = findRecordOrDefault(
    'generic',
    nodeType,
    OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY
  ) as OperationalDataLocationRecord;

  useEffect(() => {
    if (missionData != null && findNodes(nodeType).length > 0) {
      adaptRecord(
        'generic',
        nodeType,
        (draft: Draft<OperationalDataTransportLocationRecord>, deleteRecord) => {
          draft.inputState = InputState.ENTERED;

          handleUntouchedTextFieldUpdates(
            draft,
            LocationPropertyKeys.missionStreetGPSLatitude,
            missionData.missionLocation?.address?.street ??
              missionData.missionLocation?.position?.latitude?.toLocaleString(NUMBER_LANGUAGE)
          );
          handleUntouchedTextFieldUpdates(
            draft,
            LocationPropertyKeys.missionCityGPSLongitude,
            missionData.missionLocation?.address?.city ??
              missionData.missionLocation?.position?.longitude?.toLocaleString(NUMBER_LANGUAGE)
          );
          handleUntouchedTextFieldUpdates(
            draft,
            LocationPropertyKeys.missionPostcode,
            missionData.missionLocation?.address?.postalCode
          );
          handleUntouchedSingleSelectFieldUpdates(
            draft,
            LocationPropertyKeys.missionLocationType,
            mapStringToMissionLocationKey(missionData.missionLocation?.type)
          );

          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        },
        OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY
      );
    }
  }, [missionData, findNodes, nodeType, adaptRecord]);

  function handleStreetOrGPSLatitudeChange(value: string) {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
      LocationPropertyKeys.missionStreetGPSLatitude,
      value === '' ? undefined : { fieldType: 'text', text: value }
    );
  }

  function handleCityOrGPSLongitudeChange(value: string) {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
      LocationPropertyKeys.missionCityGPSLongitude,
      value === '' ? undefined : { fieldType: 'text', text: value }
    );
  }

  function handlePostalCodeChange(value: string) {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
      LocationPropertyKeys.missionPostcode,
      value === '' ? undefined : { fieldType: 'text', text: value }
    );
  }

  const handleLocationType = useFreshCallback((locationEnumValue: string | null) => {
    handleLocationTypeChange(locationEnumValue);
    handleLocationTypeSave(locationEnumValue);
  });

  function handleLocationTypeChange(locationEnumValue: string | null) {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
      LocationPropertyKeys.missionLocationType,
      locationEnumValue == null ? undefined : { fieldType: 'singleSelect', option: locationEnumValue }
    );
  }

  function handleLocationTypeSave(locationEnumValue: string | null) {
    if (ApplicationConfig.immediatelySyncToConference) {
      if (locationEnumValue != null && locationEnumValue != '') {
        const translatedLocationType = locationItems.find(([key]) => key === locationEnumValue)![1];
        missionDataUpdate?.({
          ...missionData,
          missionLocation: {
            ...missionData?.missionLocation,
            type: translatedLocationType,
          },
        });
      } else {
        missionDataUpdate?.({
          ...missionData,
          missionLocation: _.omit(missionData?.missionLocation, ['type']),
        });
      }
    }
  }

  const handlePostCodeSave = (valueToSave?: string) => {
    if (valueToSave != null && valueToSave !== '' && valueToSave !== undefined) {
      missionDataUpdate?.({
        ...missionData,
        missionLocation: {
          ...missionData?.missionLocation,
          address: {
            ...missionData?.missionLocation?.address,
            postalCode: valueToSave,
          },
        },
      });
    } else {
      missionDataUpdate?.({
        ...missionData,
        missionLocation: {
          ...missionData?.missionLocation,
          address: _.omit(missionData?.missionLocation?.address, ['postalCode']),
        },
      });
    }
  };

  const handleStreetOrGPSLatitudeSave = (valueToSave?: string) => {
    if (valueToSave != null && valueToSave !== '' && valueToSave !== undefined) {
      missionDataUpdate?.({
        ...missionData,
        missionLocation: {
          ...missionData?.missionLocation,
          address: {
            ...missionData?.missionLocation?.address,
            street: valueToSave,
          },
        },
      });
    } else {
      missionDataUpdate?.({
        ...missionData,
        missionLocation: {
          ...missionData?.missionLocation,
          address: _.omit(missionData?.missionLocation?.address, ['street']),
        },
      });
    }
  };

  const handleCityOrGPSLongitudeSave = (valueToSave?: string) => {
    if (valueToSave != null && valueToSave !== '' && valueToSave !== undefined) {
      missionDataUpdate?.({
        ...missionData,
        missionLocation: {
          ...missionData?.missionLocation,
          address: {
            ...missionData?.missionLocation?.address,
            city: valueToSave,
          },
        },
      });
    } else {
      missionDataUpdate?.({
        ...missionData,
        missionLocation: {
          ...missionData?.missionLocation,
          address: _.omit(missionData?.missionLocation?.address, ['city']),
        },
      });
    }
  };

  return (
    <DiviFieldCard cardType={CardId.MissionLocation}>
      <DebouncedTextField
        className={`no-margin-top ${getTouchedClassName(record, LocationPropertyKeys.missionStreetGPSLatitude)}`}
        label='Straße oder GPS-Breitengrad'
        fullWidth
        value={record.values?.missionStreetGPSLatitude?.text ?? ''}
        onDebounceChange={handleStreetOrGPSLatitudeChange}
        onUnfocus={handleStreetOrGPSLatitudeSave}
      />
      <DebouncedTextField
        className={getTouchedClassName(record, LocationPropertyKeys.missionCityGPSLongitude)}
        label='Ort oder GPS-Längengrad'
        fullWidth
        value={record.values?.missionCityGPSLongitude?.text ?? ''}
        onDebounceChange={handleCityOrGPSLongitudeChange}
        onUnfocus={handleCityOrGPSLongitudeSave}
      />
      <DebouncedTextField
        className={getTouchedClassName(record, LocationPropertyKeys.missionPostcode)}
        label='PLZ'
        fullWidth
        value={record.values?.missionPostcode?.text ?? ''}
        onDebounceChange={handlePostalCodeChange}
        onUnfocus={handlePostCodeSave}
      />
      <RadioList
        className={`symptoms-list add-margin-top ${getTouchedClassName(record, LocationPropertyKeys.missionLocationType)}`}
        items={locationItems}
        value={record.values?.missionLocationType?.option ?? null}
        onChange={handleLocationType}
      />
    </DiviFieldCard>
  );
}
